import React from "react";
import { Grid, Text } from "@shoptet/ui";
import { useTranslation } from "react-i18next";
import { CHART_COLORS } from "../../constants/chartColors";
import { MoneyFragmentT } from "../../graphql/generated/graphql";
import { CampaignTypeFilter } from "../../pages/DashboardTabs/Statistics";
import DetailsBox from "./DetailsBox";

interface ComparisonSummaryProps {
  campaignTypeFilter: CampaignTypeFilter;
  combinedCompareValueRaw: number | MoneyFragmentT;
  combinedCurrentValueRaw: number | MoneyFragmentT;
  googleCompareValue: string;
  googleCurrentValue: string;
  hasMetaCampaignsEnabled: boolean;
  invertArrowLogic?: boolean;
  metaCompareValue: string;
  metaCurrentValue: string;
  metaWizardFinished: boolean;
  metric: string;
  metricLabel: string;
}

export const ComparisonSummary: React.FC<ComparisonSummaryProps> = ({
  campaignTypeFilter,
  combinedCompareValueRaw,
  combinedCurrentValueRaw,
  googleCompareValue,
  googleCurrentValue,
  hasMetaCampaignsEnabled,
  invertArrowLogic = false,
  metaCompareValue,
  metaCurrentValue,
  metaWizardFinished,
  metric,
  metricLabel,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Stats" });

  const showGoogle = campaignTypeFilter === CampaignTypeFilter.ALL || campaignTypeFilter === CampaignTypeFilter.GOOGLE;
  const showMeta =
    (campaignTypeFilter === CampaignTypeFilter.ALL || campaignTypeFilter === CampaignTypeFilter.META) &&
    hasMetaCampaignsEnabled &&
    metaWizardFinished;

  // Show metric label only for first 3 metrics: impressions, clicks, conversions
  const showMetricLabel = ["impressions", "clicks", "conversions"].includes(metric);
  const labelToPass = showMetricLabel ? metricLabel : undefined;

  // Calculate percentage using same logic as getCombinedTabCompareData
  const thisAmount =
    typeof combinedCurrentValueRaw === "number"
      ? combinedCurrentValueRaw
      : (combinedCurrentValueRaw as MoneyFragmentT)?.amount;
  const lastAmount =
    typeof combinedCompareValueRaw === "number"
      ? combinedCompareValueRaw
      : (combinedCompareValueRaw as MoneyFragmentT)?.amount;

  let percentageValue = 0;
  if (thisAmount && lastAmount) {
    if (lastAmount && lastAmount !== 0) {
      percentageValue = (thisAmount - lastAmount) / lastAmount;
    } else if (thisAmount && thisAmount > 0) {
      percentageValue = 1;
    } else if (thisAmount === 0 && lastAmount && lastAmount > 0) {
      percentageValue = -1;
    }
  }

  const isPositive = invertArrowLogic ? percentageValue < 0 : percentageValue >= 0;
  const sign = percentageValue >= 0 ? "+" : "-";
  const percentageFormatted = `${sign}${(Math.abs(percentageValue) * 100).toFixed(2)}%`;
  const percentageColor = isPositive ? "var(--app-success)" : "var(--app-critical)";

  return (
    <div className="typography-mb-4" style={{ margin: "32px 0 28px" }}>
      <Grid columns={2}>
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "4px", gap: "4px" }}>
            <span>
              <Text appearance="lg">{t("Actual data")}</Text>
            </span>
            <span>
              <Text appearance="lg">
                (<span style={{ color: percentageColor }}>{percentageFormatted}</span>)
              </Text>
            </span>
          </div>
          <div style={{ display: "flex", gap: "14px" }}>
            {showGoogle && (
              <DetailsBox
                campaignType="google"
                color={CHART_COLORS.google}
                metricLabel={labelToPass}
                value={googleCurrentValue}
              />
            )}
            {showMeta && (
              <DetailsBox
                campaignType="meta"
                color={CHART_COLORS.meta}
                metricLabel={labelToPass}
                value={metaCurrentValue}
              />
            )}
          </div>
        </div>
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Text appearance="lg">{t("Compare data")}</Text>
          </div>
          <div style={{ display: "flex", gap: "14px" }}>
            {showGoogle && (
              <DetailsBox
                campaignType="google"
                color={CHART_COLORS.google}
                metricLabel={labelToPass}
                value={googleCompareValue}
              />
            )}
            {showMeta && (
              <DetailsBox
                campaignType="meta"
                color={CHART_COLORS.meta}
                metricLabel={labelToPass}
                value={metaCompareValue}
              />
            )}
          </div>
        </div>
      </Grid>
    </div>
  );
};
